import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { paymentApi, scheduleApi, singleJobApi } from '../../api/endpoints.ts';
import { ApiRequestError } from '../../api/client.ts';
import { useSession } from '../../state/session.tsx';
import { useLocalizedContent } from '../../i18n/localized.ts';
import { getDateLocale } from '../../i18n/locale.ts';
import { useAppConfig } from '../turnstile.tsx';
import { Alert, Button, Select, TextArea, TextInput, classes } from '../ui.tsx';
import { StarterNudge } from '../starter-nudge.tsx';
import { QuotePaymentBadge, quotePaymentsKey } from '../payments/quote-payments.tsx';
import { todayInTimezone } from '../schedule/format.ts';
import { formatCents } from '@shared/calculations/pricing.ts';
import { addDaysToDate } from '@shared/calculations/recurrence.ts';
import { refundableCentsForCharge } from '@shared/calculations/payments.ts';
import type { Payment, Quote, SingleJob, SingleJobActionResult } from '@shared/types/index.ts';

/** The query every screen showing a quote's job shares. */
export function singleJobKey(quoteId: string) {
  return ['single-job', quoteId] as const;
}

/** "Tue, Oct 13, 9:00 AM", in the visit's own timezone and the reader's language. */
export function formatJobStart(startsAt: string, timezone: string): string {
  try {
    return new Intl.DateTimeFormat(getDateLocale(), {
      timeZone: timezone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(startsAt));
  } catch {
    return startsAt;
  }
}

/** The calendar day of an instant, as somebody in `timeZone` reads it. */
function formatDay(iso: string, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat(getDateLocale(), {
      timeZone,
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}

const OPEN = new Set(['scheduled', 'confirmed', 'change_requested']);

/** The edits on screen could not be saved, so nothing was booked. */
class EditsNotSaved extends Error {
  constructor(readonly action: 'book' | 'change' = 'book') {
    super();
  }
}

/**
 * One job at a time, on the quote it came from (`single_job`): Book it, Do
 * the work, Get paid, then Book again. What the Free plan has in place of the
 * calendar — the same visit and invoice rows the calendar would write, so an
 * upgrade finds them there.
 *
 * Replaces "Mark the work completed" on an approved one-time quote. A
 * recurring or monthly quote says that repeat visits are on Starter instead.
 * A Book again copy shows where it came from, and whether booking it carries
 * the original approval or needs the customer's.
 */
export function SingleJobCard({
  quote,
  prepare,
}: {
  quote: Quote;
  /**
   * Run before booking, for a caller holding unsaved edits (the editor). The
   * job is booked from the quote as the server has it, so the edits on screen
   * are saved first, and a refused save books nothing.
   */
  prepare?: () => Promise<Quote | null>;
}) {
  const { t } = useTranslation();
  const localized = useLocalizedContent();
  const { can, company, currentLocation } = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const appConfig = useAppConfig();
  const [params, setParams] = useSearchParams();

  const canBook = can('schedule:write');
  const canTakePayments = can('payments:write');
  const canRefund = can('payments:refund');
  const canConnect = can('company:write');
  // The working location's calendar, which the server checks a booking's day
  // against: a location can keep a timezone of its own.
  const timezone = currentLocation?.timezone ?? company?.timezone ?? 'UTC';

  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [day, setDay] = useState(() => addDaysToDate(todayInTimezone(timezone), 1));
  const [time, setTime] = useState('09:00');
  const [length, setLength] = useState('');
  const [changing, setChanging] = useState(false);
  const [recording, setRecording] = useState(false);
  const [methodId, setMethodId] = useState('');
  const [note, setNote] = useState('');
  const [setupOpen, setSetupOpen] = useState(false);
  const [photoOverride, setPhotoOverride] = useState(false);
  const [photoOverrideReason, setPhotoOverrideReason] = useState('');
  // The last payment link made here, with the payment it is: offered to copy
  // only while the job still reports that same link open.
  const [link, setLink] = useState<{ url: string; paymentId: string | null } | null>(null);

  const job = useQuery({
    queryKey: singleJobKey(quote.id),
    queryFn: () => singleJobApi.get(quote.id),
    // A customer pays a link on Stripe's page, out of sight of this one: while
    // one is open, look again now and then so a paid job shows as paid.
    refetchInterval: (query) => (query.state.data?.openLink ? 30_000 : false),
  });
  const completionChecklist = useQuery({
    queryKey: ['visit-checklist', job.data?.visit?.appointmentId],
    queryFn: () => scheduleApi.checklist(job.data!.visit!.appointmentId),
    enabled: Boolean(
      job.data?.visit && OPEN.has(job.data.visit.status) && company?.planFeatures?.includes('checklist_reports'),
    ),
    refetchInterval: 5000,
  });
  const missingPhotos = completionChecklist.data?.missingRequiredPhotos ?? 0;
  const photosBlocked = Boolean(completionChecklist.data?.blockCompletionOnMissingPhotos && missingPhotos > 0);
  const photoCheckFailed =
    completionChecklist.isError &&
    !(completionChecklist.error instanceof ApiRequestError && completionChecklist.error.status === 404);
  const methods = useQuery({
    queryKey: ['payment-methods', 'active'],
    queryFn: () => paymentApi.listMethods(),
    enabled: canTakePayments && recording,
  });

  // Back from Stripe: say how it went, once, and take the flag off the URL.
  useEffect(() => {
    const outcome = params.get('stripeConnect');
    if (!outcome) return;
    if (outcome === 'linked') setNotice(t('singleJob.connected'));
    else setError(t('singleJob.connectProblem'));
    const next = new URLSearchParams(params);
    next.delete('stripeConnect');
    setParams(next, { replace: true });
  }, [params, setParams, t]);

  const data = job.data;
  const money = (cents: number) => formatCents(cents, data?.money.currencyCode ?? quote.currencyCode, getDateLocale());

  function refresh(next?: SingleJob) {
    if (next) queryClient.setQueryData(singleJobKey(quote.id), next);
    void queryClient.invalidateQueries({ queryKey: singleJobKey(quote.id) });
    void queryClient.invalidateQueries({ queryKey: ['quote', quote.id] });
    void queryClient.invalidateQueries({ queryKey: quotePaymentsKey(quote.id) });
    void queryClient.invalidateQueries({ queryKey: ['booked-jobs'] });
    void queryClient.invalidateQueries({ queryKey: ['owed'] });
    void queryClient.invalidateQueries({ queryKey: ['customer-payments', quote.customerId] });
  }

  function failed(caught: unknown) {
    setNotice(null);
    if (caught instanceof EditsNotSaved) {
      setError(t(caught.action === 'change' ? 'singleJob.saveFirstFailedChange' : 'singleJob.saveFirstFailed'));
      return;
    }
    if (caught instanceof ApiRequestError && caught.code === 'CARD_PAYMENTS_NOT_SET_UP') {
      setSetupOpen(true);
      setError(null);
      return;
    }
    if (caught instanceof ApiRequestError && caught.code === 'CUSTOMER_APPROVAL_NEEDED') {
      const reason = caught.fields.reason;
      setError(
        t(
          `singleJob.needsApproval.${reason ?? 'source_not_approved'}`,
          t('singleJob.needsApproval.source_not_approved'),
        ),
      );
      void queryClient.invalidateQueries({ queryKey: singleJobKey(quote.id) });
      return;
    }
    setError(caught instanceof ApiRequestError ? caught.message : t('singleJob.actionFailed'));
  }

  function succeeded(next: SingleJob, message: string) {
    setError(null);
    setNotice(message);
    refresh(next);
  }

  /**
   * Done, and said so, unless its email to the customer did not go out: then
   * the owner hears that instead, so they can tell the customer themselves.
   */
  function notified(next: SingleJobActionResult, kind: 'booked' | 'changed' | 'cancelled', message: string) {
    const notSentTo = next.customerNotice?.notSentTo;
    if (!notSentTo) {
      succeeded(next, message);
      return;
    }
    refresh(next);
    setNotice(null);
    setError(t(`singleJob.noticeNotSent.${kind}`, { email: notSentTo }));
  }

  const book = useMutation({
    mutationFn: async () => {
      if (prepare && !(await prepare())) throw new EditsNotSaved();
      return singleJobApi.book(quote.id, {
        startsOn: day,
        startTimeLocal: time,
        ...(length ? { durationMinutes: Number(length) } : {}),
      });
    },
    onSuccess: (next) => notified(next, 'booked', t('singleJob.booked')),
    onError: failed,
  });
  const reschedule = useMutation({
    mutationFn: async () => {
      // Cleared, the length goes back to the quote's estimate, as its hint
      // says: the estimate the server works out from the quote as saved, so
      // edits still waiting on the autosave are saved first, as booking does.
      if (!length && prepare && !(await prepare())) throw new EditsNotSaved('change');
      return singleJobApi.reschedule(quote.id, {
        scheduledDate: day,
        startTimeLocal: time,
        durationMinutes: length ? Number(length) : null,
      });
    },
    onSuccess: (next) => {
      setChanging(false);
      // Only a contact with an address hears about it.
      notified(
        next,
        'changed',
        next.customerNotice?.emailedTo ? t('singleJob.changed') : t('singleJob.changedNoEmail'),
      );
    },
    onError: failed,
  });
  const cancel = useMutation({
    mutationFn: () => singleJobApi.cancel(quote.id),
    onSuccess: (next) => notified(next, 'cancelled', t('singleJob.cancelledNotice')),
    onError: failed,
  });
  const complete = useMutation({
    mutationFn: () =>
      photosBlocked && photoOverride
        ? singleJobApi.complete(quote.id, photoOverrideReason.trim(), completionChecklist.data?.revision)
        : singleJobApi.complete(quote.id, undefined, completionChecklist.data?.revision),
    onSuccess: (next) => succeeded(next, t('singleJob.completedNotice')),
    onError: failed,
  });
  const sendLink = useMutation({
    mutationFn: (email: boolean) => singleJobApi.paymentLink(quote.id, email),
    onSuccess: (result) => {
      setLink({ url: result.url, paymentId: result.job.openLink?.paymentId ?? null });
      if (result.notSentTo) {
        // Made, but the email did not go: the link below is the way to share it.
        refresh(result.job);
        setNotice(null);
        setError(t('singleJob.linkNotSent', { email: result.notSentTo }));
        return;
      }
      succeeded(
        result.job,
        result.emailedTo ? t('singleJob.linkSent', { email: result.emailedTo }) : t('singleJob.linkMade'),
      );
    },
    onError: failed,
  });
  const record = useMutation({
    mutationFn: () =>
      singleJobApi.recordPayment(quote.id, { methodId, ...(note.trim() ? { description: note.trim() } : {}) }),
    onSuccess: (next) => {
      setRecording(false);
      setNote('');
      setLink(null);
      succeeded(next, t('singleJob.recorded'));
    },
    onError: failed,
  });
  const connect = useMutation({
    mutationFn: () => paymentApi.connectStart(undefined, { returnPath: `/quotes/${quote.id}` }),
    onSuccess: (result) => window.location.assign(result.url),
    onError: failed,
  });
  const refund = useMutation({
    mutationFn: (paymentId: string) => paymentApi.refund(paymentId, {}),
    onSuccess: () => {
      setError(null);
      setNotice(t('singleJob.refunded'));
      refresh();
    },
    onError: failed,
  });
  const again = useMutation({
    mutationFn: () => singleJobApi.bookAgain(quote.id),
    onSuccess: (copy) => navigate(`/quotes/${copy.id}?bookAgain=1`),
    onError: failed,
  });

  if (job.isLoading) return null;
  if (job.isError || !data) {
    return (
      <section className="card mb-6">
        <Alert tone="error">{t('singleJob.loadError')}</Alert>
      </section>
    );
  }

  const visit = data.visit;
  const live = visit && visit.status !== 'cancelled' ? visit : null;
  const open = live ? OPEN.has(live.status) : false;
  const done = live?.status === 'completed';
  const approved = quote.status === 'accepted' || quote.status === 'completed';
  const copy = data.bookAgain;
  const paidByLink = data.visitPayments.some(
    (payment) => payment.type === 'charge' && payment.method === 'checkout_link' && payment.status === 'succeeded',
  );
  // Money taken can be given back whatever became of the job: a payment taken
  // by mistake on a job still booked or done as much as one on a canceled job.
  const refunds = refundableCharges(data.visitPayments);
  const nothingToPay = data.money.status !== 'paid' && data.money.balanceCents <= 0;
  // A link canceled with its visit, replaced or paid is never put up again.
  const linkUrl = link?.paymentId && data.openLink?.paymentId === link.paymentId ? link.url : null;
  const carried = quote.approvalCarryover;
  const carriedDate = carried?.acceptedAt ? formatDay(carried.acceptedAt, timezone) : null;
  const offerBookAgain = data.canBookAgain;
  // One Starter line at a time, about what just happened here: a link that
  // was paid, a customer booked again for the third time in 90 days (once per
  // customer), or else the calendar.
  const nudge: { id: string; message: string; once?: boolean } = paidByLink
    ? { id: 'single-job-reminders', message: t('singleJob.starter.paidLink') }
    : copy && copy.copiesInLast90Days >= 3
      ? { id: `single-job-repeat-${quote.customerId}`, message: t('singleJob.starter.repeat'), once: true }
      : { id: 'single-job-calendar', message: t('singleJob.starter.booking') };

  function openChange() {
    if (!live) return;
    setDay(live.scheduledDate);
    setTime(live.startTimeLocal);
    setLength(String(live.durationMinutes));
    setChanging(true);
  }

  function submitBook(event: FormEvent) {
    event.preventDefault();
    if (!day) return;
    if (changing) reschedule.mutate();
    else book.mutate();
  }

  const dateFields = (
    <div className="grid gap-x-4 sm:grid-cols-3">
      <TextInput
        label={t('singleJob.dateLabel')}
        type="date"
        value={day}
        min={todayInTimezone(timezone)}
        onChange={(event) => setDay(event.target.value)}
        required
      />
      <TextInput
        label={t('singleJob.timeLabel')}
        type="time"
        value={time}
        onChange={(event) => setTime(event.target.value)}
        required
      />
      <TextInput
        label={t('singleJob.lengthLabel')}
        hint={t('singleJob.lengthHint')}
        type="number"
        min={15}
        max={1440}
        step={15}
        value={length}
        onChange={(event) => setLength(event.target.value)}
      />
    </div>
  );

  // A recurring or monthly quote is not a single job.
  if (!data.oneTime) {
    return (
      <section className="card mb-6" aria-labelledby="single-job-heading">
        <h2 id="single-job-heading" className="mb-2 text-lg font-semibold">
          {t('singleJob.heading')}
        </h2>
        <p className="text-sm text-[color:var(--color-muted)]">{t('singleJob.repeatOnStarter')}</p>
        <Link to="/settings#settings-billing" className="mt-2 inline-block text-sm font-semibold underline">
          {t('starterNudge.cta', { plan: 'Starter' })}
        </Link>
      </section>
    );
  }

  return (
    <section className="card mb-6" aria-labelledby="single-job-heading">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 id="single-job-heading" className="text-lg font-semibold">
          {t('singleJob.heading')}
        </h2>
        {/* Book again makes a copy to review and book later, so it needs what
            copying a quote needs, as POST /book-again asks. Booking that copy
            asks for the schedule permission then, on the copy's own card. */}
        {offerBookAgain && can('quotes:write') ? (
          <Button busy={again.isPending} onClick={() => again.mutate()} title={t('singleJob.bookAgainHint')}>
            {t('singleJob.bookAgain')}
          </Button>
        ) : null}
      </div>

      {copy ? (
        <p className="mb-3 text-sm">
          {t('singleJob.bookedAgainFrom')}{' '}
          {copy.source ? (
            <Link to={`/quotes/${copy.source.id}`} className="font-semibold text-[color:var(--color-brand)] underline">
              {copy.source.quoteNumber}
            </Link>
          ) : null}
        </p>
      ) : null}
      {carried ? (
        <p className="mb-3 text-sm text-[color:var(--color-muted)]">
          {/* With no date on record it says who, never "approved on ." */}
          {carried.acceptedByName
            ? carriedDate
              ? t('singleJob.carryover', {
                  number: carried.fromQuoteNumber,
                  date: carriedDate,
                  name: carried.acceptedByName,
                })
              : t('singleJob.carryoverUndated', { number: carried.fromQuoteNumber, name: carried.acceptedByName })
            : carriedDate
              ? t('singleJob.carryoverNoName', { number: carried.fromQuoteNumber, date: carriedDate })
              : t('singleJob.carryoverUndatedNoName', { number: carried.fromQuoteNumber })}
        </p>
      ) : null}

      {error ? (
        <div className="mb-3">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}
      {notice ? (
        <div className="mb-3">
          <Alert tone="success">{notice}</Alert>
        </div>
      ) : null}

      <ol className="grid gap-4">
        {/* ---- 1. Book it --------------------------------------------------- */}
        <Step number={1} title={t('singleJob.stepBook')} done={Boolean(live)} active={!live}>
          {live && !changing ? (
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-semibold">
                {t('singleJob.bookedFor', { when: formatJobStart(live.startsAt, live.timezone) })}
              </p>
              {open && canBook ? (
                <>
                  <Button variant="ghost" onClick={openChange}>
                    {t('singleJob.change')}
                  </Button>
                  <Button
                    variant="ghost"
                    busy={cancel.isPending}
                    onClick={() => {
                      const question = data.noticeEmail
                        ? t('singleJob.confirmCancel')
                        : t('singleJob.confirmCancelNoEmail');
                      if (window.confirm(question)) cancel.mutate();
                    }}
                  >
                    {t('singleJob.cancel')}
                  </Button>
                </>
              ) : null}
            </div>
          ) : null}

          {visit?.status === 'cancelled' && !live ? (
            <p className="mb-2 text-sm text-[color:var(--color-muted)]">
              {t('singleJob.cancelled', { when: formatJobStart(visit.startsAt, visit.timezone) })}
            </p>
          ) : null}

          {copy && !approved && copy.needsApproval ? (
            <div className="mb-2">
              <Alert tone="warning">
                <p>{t(`singleJob.needsApproval.${copy.needsApproval}`)}</p>
                <p className="mt-1 text-sm">{t('singleJob.needsApprovalHint')}</p>
              </Alert>
            </div>
          ) : copy && !approved && !copy.needsApproval ? (
            <p className="mb-2 text-sm text-[color:var(--color-muted)]">{t('singleJob.noApprovalNeeded')}</p>
          ) : null}

          {(data.canBook && canBook && !live) || changing ? (
            <form onSubmit={submitBook} className="grid gap-2">
              {!changing ? (
                <p className="text-sm text-[color:var(--color-muted)]">
                  {data.noticeEmail ? t('singleJob.bookIntro') : t('singleJob.bookIntroNoEmail')}
                </p>
              ) : null}
              {dateFields}
              <div className="flex flex-wrap gap-2">
                <Button type="submit" variant="primary" busy={book.isPending || reschedule.isPending}>
                  {changing ? t('singleJob.changeSubmit') : t('singleJob.bookSubmit')}
                </Button>
                {changing ? (
                  <Button variant="ghost" onClick={() => setChanging(false)}>
                    {t('singleJob.changeKeep')}
                  </Button>
                ) : null}
              </div>
            </form>
          ) : null}
        </Step>

        {/* ---- 2. Do the work ------------------------------------------------ */}
        <Step number={2} title={t('singleJob.stepWork')} done={done} active={open}>
          {done && live?.completedAt ? (
            <p className="text-sm">
              {t('singleJob.completedOn', { date: formatDay(live.completedAt, live.timezone) })}
            </p>
          ) : open && !approved ? (
            // Being revised: done waits for the customer's approval, as payment does.
            <p className="text-sm text-[color:var(--color-muted)]">{t('singleJob.workWaitingApproval')}</p>
          ) : open ? (
            canBook ? (
              <div>
                {photosBlocked ? (
                  <Alert tone="warning">{t('checklists.missingPhotos', { count: missingPhotos })}</Alert>
                ) : null}
                {photoCheckFailed ? <Alert tone="error">{t('checklists.completionCheckFailed')}</Alert> : null}
                {photosBlocked && can('checklists:override') ? (
                  <div>
                    <label>
                      <input
                        type="checkbox"
                        checked={photoOverride}
                        onChange={(event) => setPhotoOverride(event.target.checked)}
                      />
                      {t('checklists.completeAnyway')}
                    </label>
                    {photoOverride ? (
                      <TextArea
                        label={t('checklists.overrideReason')}
                        value={photoOverrideReason}
                        onChange={(event) => setPhotoOverrideReason(event.target.value)}
                        maxLength={2000}
                      />
                    ) : null}
                  </div>
                ) : null}
                <Button
                  busy={complete.isPending}
                  disabled={
                    completionChecklist.isLoading ||
                    photoCheckFailed ||
                    (photosBlocked && (!can('checklists:override') || !photoOverride || !photoOverrideReason.trim()))
                  }
                  onClick={() => complete.mutate()}
                >
                  {photosBlocked && photoOverride ? t('checklists.completeAnyway') : t('singleJob.markCompleted')}
                </Button>
              </div>
            ) : null
          ) : (
            <p className="text-sm text-[color:var(--color-muted)]">{t('singleJob.workWaiting')}</p>
          )}
        </Step>

        {/* ---- 3. Get paid ---------------------------------------------------- */}
        <Step
          number={3}
          title={t('singleJob.stepPaid')}
          done={data.money.status === 'paid' || (Boolean(live) && nothingToPay)}
          active={Boolean(live)}
        >
          <div className="mb-2 flex flex-wrap items-center gap-2">
            {nothingToPay ? (
              // A job priced at nothing: no badge saying Unpaid, and nothing to send.
              <span className="text-sm">{t('singleJob.nothingToPay')}</span>
            ) : (
              <>
                <QuotePaymentBadge status={data.money.status} />
                {data.money.status === 'paid' ? (
                  <span className="text-sm">{t('singleJob.paidInFull')}</span>
                ) : (
                  <span className="text-sm font-semibold">
                    {t('singleJob.amountDue', { amount: money(data.money.balanceCents) })}
                  </span>
                )}
              </>
            )}
          </div>
          {data.money.paidOnQuoteCents > 0 && data.money.status !== 'paid' ? (
            <p className="mb-2 text-sm text-[color:var(--color-muted)]">
              {t('singleJob.partlyNote', {
                paid: money(data.money.paidOnQuoteCents),
                balance: money(data.money.balanceCents),
              })}
            </p>
          ) : null}

          {live && data.money.status !== 'paid' && !nothingToPay && canTakePayments && approved ? (
            <>
              {data.openLink ? (
                <p className="mb-2 text-sm text-[color:var(--color-muted)]">
                  {t('singleJob.linkOpen', { amount: money(data.openLink.amountCents) })}
                </p>
              ) : null}
              <div className="flex flex-wrap gap-2">
                {data.customerEmail ? (
                  <Button
                    variant={done ? 'primary' : 'secondary'}
                    busy={sendLink.isPending && sendLink.variables === true}
                    onClick={() => (data.cardPaymentsReady ? sendLink.mutate(true) : setSetupOpen(true))}
                  >
                    {data.openLink ? t('singleJob.sendLinkAgain') : t('singleJob.sendLink')}
                  </Button>
                ) : null}
                <Button
                  variant={data.customerEmail ? 'ghost' : done ? 'primary' : 'secondary'}
                  busy={sendLink.isPending && sendLink.variables === false}
                  onClick={() => (data.cardPaymentsReady ? sendLink.mutate(false) : setSetupOpen(true))}
                >
                  {t('singleJob.makeLink')}
                </Button>
                {!recording ? (
                  <Button variant="ghost" onClick={() => setRecording(true)}>
                    {t('singleJob.recordPayment')}
                  </Button>
                ) : null}
              </div>
              {data.customerEmail ? (
                <p className="mt-1 text-xs text-[color:var(--color-muted)]">
                  {t('singleJob.sendLinkHint', { email: data.customerEmail, amount: money(data.money.balanceCents) })}
                </p>
              ) : (
                <p className="mt-1 text-xs text-[color:var(--color-muted)]">{t('singleJob.noEmailHint')}</p>
              )}

              {linkUrl ? (
                <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-[color:var(--color-line)] p-2">
                  <input
                    readOnly
                    aria-label={t('singleJob.linkLabel')}
                    className="field-control min-w-0 flex-1"
                    value={linkUrl}
                    onFocus={(event) => event.currentTarget.select()}
                  />
                  <Button
                    variant="ghost"
                    onClick={() => {
                      void navigator.clipboard?.writeText(linkUrl).then(
                        () => setNotice(t('singleJob.linkCopied')),
                        () => undefined,
                      );
                    }}
                  >
                    {t('singleJob.copyLink')}
                  </Button>
                </div>
              ) : null}

              {setupOpen && !data.cardPaymentsReady ? (
                <div className="mt-3 rounded-md border border-[color:var(--color-line)] p-3">
                  <p className="font-semibold">{t('singleJob.setupTitle')}</p>
                  <p className="mt-1 text-sm text-[color:var(--color-muted)]">{t('singleJob.setupBody')}</p>
                  {appConfig.data?.stripeConnectAvailable === false ? (
                    <p className="mt-2 text-sm">{t('singleJob.setupUnavailable')}</p>
                  ) : canConnect ? (
                    <Button
                      className="mt-2"
                      variant="primary"
                      busy={connect.isPending}
                      onClick={() => connect.mutate()}
                    >
                      {t('singleJob.setupConnect')}
                    </Button>
                  ) : (
                    <p className="mt-2 text-sm">{t('singleJob.setupOwnerOnly')}</p>
                  )}
                </div>
              ) : null}

              {recording ? (
                <form
                  className="mt-3 grid gap-2 rounded-md border border-[color:var(--color-line)] p-3 sm:grid-cols-2"
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (methodId) record.mutate();
                  }}
                >
                  <p className="text-sm sm:col-span-2">
                    {t('singleJob.recordHint', { amount: money(data.money.balanceCents) })}
                  </p>
                  <Select
                    label={t('quotePayments.typeLabel')}
                    value={methodId}
                    onChange={(event) => setMethodId(event.target.value)}
                    options={[
                      { value: '', label: t('quotePayments.typePlaceholder') },
                      ...(methods.data?.items ?? [])
                        .filter((method) => method.isActive)
                        .map((method) => ({
                          value: method.id,
                          label: localized.text(method.name, method.nameTranslations),
                        })),
                    ]}
                  />
                  <TextInput
                    label={t('quotePayments.noteLabel')}
                    value={note}
                    maxLength={200}
                    placeholder={t('quotePayments.notePlaceholder')}
                    onChange={(event) => setNote(event.target.value)}
                  />
                  <div className="flex flex-wrap gap-2 sm:col-span-2">
                    <Button type="submit" variant="primary" busy={record.isPending} disabled={!methodId}>
                      {t('singleJob.recordSubmit', { amount: money(data.money.balanceCents) })}
                    </Button>
                    <Button variant="ghost" onClick={() => setRecording(false)}>
                      {t('quotePayments.cancel')}
                    </Button>
                  </div>
                </form>
              ) : null}
            </>
          ) : live && !approved && data.money.status !== 'paid' && !nothingToPay ? (
            <p className="text-sm text-[color:var(--color-muted)]">{t('singleJob.waitingApproval')}</p>
          ) : !live ? (
            // Before booking, an approved quote can still take money on the
            // quote below; one waiting for approval (a Book again copy) has
            // nowhere to put it yet.
            <p className="text-sm text-[color:var(--color-muted)]">
              {approved ? t('singleJob.payWaiting') : t('singleJob.workWaiting')}
            </p>
          ) : null}

          {refunds.length > 0 && canRefund ? (
            <ul className="mt-3 grid gap-2">
              {live ? (
                <li className="text-sm text-[color:var(--color-muted)]">{t('singleJob.refundLiveIntro')}</li>
              ) : null}
              {refunds.map(({ payment, refundable }) => (
                <li
                  key={payment.id}
                  className="flex flex-wrap items-center gap-2 rounded-md border border-[color:var(--color-line)] p-2"
                >
                  {payment.method === 'manual' ? (
                    // Cash or a check: the office hands it back, and records that here.
                    <>
                      <span className="text-sm">
                        {t('singleJob.recordedRefundPrompt', {
                          amount: money(refundable),
                          method: payment.manualMethodName ?? t('singleJob.recordedRefundMethod'),
                        })}
                      </span>
                      <Button
                        variant={live ? 'ghost' : 'primary'}
                        busy={refund.isPending && refund.variables === payment.id}
                        onClick={() => {
                          if (window.confirm(t('singleJob.confirmRecordedRefund', { amount: money(refundable) }))) {
                            refund.mutate(payment.id);
                          }
                        }}
                      >
                        {t('quotePayments.refundSubmit')}
                      </Button>
                    </>
                  ) : (
                    <span className="text-sm">{t('singleJob.refundPrompt', { amount: money(refundable) })}</span>
                  )}
                  {payment.method === 'manual' ? null : payment.providerAccountConnected ? (
                    <Button
                      variant={live ? 'ghost' : 'primary'}
                      busy={refund.isPending && refund.variables === payment.id}
                      onClick={() => {
                        // Money leaves the account: asked first, like every other refund.
                        if (window.confirm(t('singleJob.confirmRefund', { amount: money(refundable) }))) {
                          refund.mutate(payment.id);
                        }
                      }}
                    >
                      {t('singleJob.refundButton')}
                    </Button>
                  ) : (
                    // The account it went to is gone, so a refund would only be refused.
                    <span className="text-sm text-[color:var(--color-muted)]">
                      {t('singleJob.refundAccountDisconnected')}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          ) : null}
        </Step>
      </ol>

      <StarterNudge key={nudge.id} className="mt-4" id={nudge.id} once={nudge.once} message={nudge.message} />
    </section>
  );
}

function Step(props: { number: number; title: string; done: boolean; active: boolean; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span
        aria-hidden="true"
        className={classes(
          'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-sm font-semibold',
          props.done
            ? 'border-[color:var(--color-brand)] bg-[color:var(--color-brand)] text-white'
            : props.active
              ? 'border-[color:var(--color-brand)] text-[color:var(--color-brand-strong)]'
              : 'border-[color:var(--color-line)] text-[color:var(--color-muted)]',
        )}
      >
        {props.done ? '✓' : props.number}
      </span>
      <div className="min-w-0 flex-1">
        <h3 className="mb-1 font-semibold">{props.title}</h3>
        {props.children}
      </div>
    </li>
  );
}

/**
 * The payments on the job's visit that still have money to give back: a
 * card's, refunded through the account it went to, and cash, a check or
 * another type the office recorded, whose refund is a record of handing it
 * back. Free has no other page that offers either.
 */
function refundableCharges(payments: readonly Payment[]): Array<{ payment: Payment; refundable: number }> {
  return payments
    .filter((payment) => payment.type === 'charge' && payment.status === 'succeeded')
    .map((payment) => ({ payment, refundable: refundableCentsForCharge(payment.id, payments) }))
    .filter((entry) => entry.refundable > 0);
}
